import { useContext, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useAgentContext } from "@copilotkit/react-core/v2";
import { useAuth } from "@/contexts/AuthContext";
import { useMyPriorities } from "@/hooks/useMyPriorities";
import { Priority } from "@/types";
import { CopilotEntityContext } from "./CopilotEntityContext";
import { getPageLabel } from "./copilotRouteLabels";

const MAX_QUEUE_ITEMS = 5;
const MAX_NAME_LENGTH = 80;

function truncate(text: string, maxLength: number) {
  return text.length > maxLength ? `${text.slice(0, maxLength - 1)}…` : text;
}

/** Same 2-line "is this priority assigned to me, and what's my status" filter as MyOneThing.tsx — duplicated here per YAGNI rather than shared, since it's this small. */
function getPersonalStatus(priority: Priority, userPid?: string) {
  const assignment = priority.assignments?.find((a) => a.assignedUser.pid === userPid);
  return assignment?.status ?? priority.status;
}

/**
 * Single ambient context bridge for the copilot agent. Registers exactly ONE
 * `useAgentContext` entry per render, aggregating (most-important-first):
 * 1) currently open entity, 2) current page, 3) my-onething focus/queue,
 * 4) user identity — so gateway tail-truncation trims the least useful text.
 *
 * Reads only from useMyPriorities (TanStack Query cache), useAuth, and
 * useLocation — never fetches directly. Renders no DOM.
 */
export function CopilotAmbientContextBridge() {
  const location = useLocation();
  const { user } = useAuth();
  const entityContext = useContext(CopilotEntityContext);
  const openEntity = entityContext?.openEntity ?? null;
  const prioritiesQuery = useMyPriorities(user?.pid);

  const entityLine = openEntity?.value ?? null;

  const pageLine = useMemo(() => `Current page: ${getPageLabel(location.pathname)}`, [location.pathname]);

  const myOneThingLine = useMemo(() => {
    const priorities = prioritiesQuery.data?.data ?? [];
    const focus = priorities.find((priority) => getPersonalStatus(priority, user?.pid) === "focus");
    const queue = priorities
      .filter((priority) => getPersonalStatus(priority, user?.pid) === "queue")
      .slice(0, MAX_QUEUE_ITEMS)
      .map((priority) => truncate(`${priority.code} ${priority.name}`, MAX_NAME_LENGTH));

    const focusText = focus ? truncate(`${focus.code} ${focus.name}`, MAX_NAME_LENGTH) : "none";
    const queueText = queue.length > 0 ? queue.join("; ") : "empty";

    return `My OneThing focus: ${focusText}. Queue (up to ${MAX_QUEUE_ITEMS}): ${queueText}.`;
  }, [prioritiesQuery.data?.data, user?.pid]);

  const userPid = user?.pid;
  const userFullName = user?.fullName;
  const userLine = useMemo(
    () => (userPid && userFullName ? `Signed in as: ${truncate(userFullName, MAX_NAME_LENGTH)} (${userPid}).` : null),
    [userPid, userFullName],
  );

  const value = useMemo(
    () => [entityLine, pageLine, myOneThingLine, userLine].filter(Boolean).join("\n"),
    [entityLine, pageLine, myOneThingLine, userLine],
  );

  useAgentContext({ description: "OneThing app state", value });

  return null;
}
